import { useCallback, useId, useRef, useState } from "react";
import { extractDoc } from "./extract-api";
import { useToast } from "../../ui/Toast";

interface Props {
  sessionId: string;
  workflowName?: string;
  /** Opened during the canvas walk (not the initial step) — adjusts copy. */
  midBuild?: boolean;
  onContinue: () => void;
  onSkip: () => void;
}

type Phase =
  | { kind: "idle" }
  | { kind: "selected"; file: File; pages?: number; tokens?: number }
  | { kind: "extracting" }
  | { kind: "done"; count: number; fileName: string; skipped: number }
  | { kind: "error"; message: string };

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = /\.(pdf|txt|md)$/i;
const MODEL_LABEL = "claude-haiku-4-5-20251001";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function estimateTokens(bytes: number): string {
  const tok = Math.round(bytes / 4 / 1000);
  return tok > 0 ? `~${tok}K tokens` : "<1K tokens";
}

function validate(file: File): string | null {
  if (!ALLOWED.test(file.name)) {
    return `Unsupported file type. We accept PDF, TXT, or MD — got "${file.name}".`;
  }
  if (file.size > MAX_BYTES) {
    return `File is too large (${formatBytes(file.size)}). Limit is 5 MB.`;
  }
  if (file.size === 0) {
    return `That file looks empty. Pick another?`;
  }
  return null;
}

export function DocUploadStep({ sessionId, workflowName, midBuild, onContinue, onSkip }: Props) {
  const toast = useToast();
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [dragOver, setDragOver] = useState(false);

  const acceptFile = useCallback((file: File) => {
    const err = validate(file);
    if (err) {
      setPhase({ kind: "error", message: err });
      return;
    }
    setPhase({ kind: "selected", file, tokens: undefined });
  }, []);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) acceptFile(f);
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const openPicker = () => fileInputRef.current?.click();

  const onExtract = async () => {
    if (phase.kind !== "selected") return;
    const file = phase.file;
    setPhase({ kind: "extracting" });
    try {
      const result = await extractDoc(sessionId, file);
      const count = Object.keys(result.drafts ?? {}).length;
      setPhase({ kind: "done", count, fileName: file.name, skipped: result.skippedAnswered ?? 0 });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Couldn't read your document. Try another?";
      setPhase({ kind: "error", message });
      toast.error(message);
    }
  };

  const resetToIdle = () => setPhase({ kind: "idle" });

  return (
    <main className="doc-upload">
      <header className="doc-upload__head">
        <p className="doc-upload__eyebrow mono">
          Filing{workflowName ? ` · ${workflowName}` : ""}
        </p>
        <h1 className="doc-upload__title">Got an existing doc?</h1>
        <p className="doc-upload__lede">
          Drop in a design doc, runbook, or spec and we'll pre-fill draft answers for
          the canvas questions we can find. Every draft surfaces inline during the
          walk — confirm, edit, or discard it. We never auto-commit your words.
        </p>
      </header>

      {phase.kind !== "extracting" && phase.kind !== "done" && (
        <>
          <label
            htmlFor={inputId}
            className={`doc-upload__drop${dragOver ? " is-dragover" : ""}${
              phase.kind === "selected" ? " has-file" : ""
            }`}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={(e) => {
              if (e.target !== fileInputRef.current) {
                e.preventDefault();
                openPicker();
              }
            }}
          >
            <input
              id={inputId}
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
              className="doc-upload__input"
              onChange={onPick}
            />
            <span className="doc-upload__glyph" aria-hidden>¶</span>
            {phase.kind === "selected" ? (
              <span className="doc-upload__drop-label">
                <span className="doc-upload__filename">{phase.file.name}</span>
                <span className="doc-upload__filemeta mono">
                  {formatBytes(phase.file.size)}
                </span>
              </span>
            ) : (
              <span className="doc-upload__drop-label">
                <span className="doc-upload__drop-cta">
                  Drop a file or click to pick one
                </span>
                <span className="doc-upload__drop-hint mono">
                  PDF · TXT · MD · up to 5 MB · 30 pages
                </span>
              </span>
            )}
          </label>

          {phase.kind === "selected" && (
            <div className="doc-upload__preflight" data-testid="doc-upload-preflight">
              <div className="doc-upload__preflight-line mono">
                <span>{phase.file.name}</span>
                <span className="doc-upload__sep">·</span>
                <span>{formatBytes(phase.file.size)}</span>
                <span className="doc-upload__sep">·</span>
                <span>{estimateTokens(phase.file.size)}</span>
              </div>
              <div className="doc-upload__preflight-actions">
                <button type="button" className="btn btn--ghost" onClick={resetToIdle}>
                  Replace file
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={onExtract}
                >
                  Extract drafts →
                </button>
              </div>
            </div>
          )}

          {phase.kind === "error" && (
            <div className="banner banner--error" role="alert">
              {phase.message}
            </div>
          )}
        </>
      )}

      {phase.kind === "extracting" && (
        <div className="doc-upload__working" role="status" aria-live="polite">
          <span className="glyph" aria-hidden />
          <span className="doc-upload__working-label">Reading your doc…</span>
          <span className="doc-upload__working-model mono">Using {MODEL_LABEL}</span>
        </div>
      )}

      {phase.kind === "done" && (
        <div className="doc-upload__result" data-testid="doc-upload-result">
          <span className="doc-upload__result-mark" aria-hidden>✓</span>
          {phase.count > 0 ? (
            <p className="doc-upload__result-text">
              <strong className="mono">{phase.count}</strong>{" "}
              draft{phase.count === 1 ? "" : "s"} found from{" "}
              <em>{phase.fileName}</em>.
            </p>
          ) : (
            <p className="doc-upload__result-text">
              Couldn't extract anything new from <em>{phase.fileName}</em>.
              Walk through the canvas as usual.
            </p>
          )}
          {phase.skipped > 0 && (
            <p className="doc-upload__result-text doc-upload__result-note">
              {phase.skipped} question{phase.skipped === 1 ? "" : "s"} you'd already
              answered {phase.skipped === 1 ? "was" : "were"} left unchanged.
            </p>
          )}
          <button
            type="button"
            className="btn btn--primary btn--large"
            onClick={onContinue}
          >
            {midBuild ? "Back to your canvas →" : "Continue to S1 →"}
          </button>
        </div>
      )}

      <footer className="doc-upload__foot">
        <button type="button" className="btn btn--text" onClick={onSkip}>
          {midBuild ? "← Back to your canvas" : "Skip — start from scratch →"}
        </button>
      </footer>
    </main>
  );
}

export default DocUploadStep;
